"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/supabase/client";
import { isInLocalWatchlist, removeLocalWatchlistSymbol } from "@/lib/local-portfolio/store";
import { useAuthModal } from "@/components/auth/auth-modal-context";
import { cn } from "@/lib/utils";

export function WatchlistStarButton({ symbol }: { symbol: string }) {
  const router = useRouter();
  const { openAuthModal } = useAuthModal();
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  const [isSaved, setIsSaved] = useState(false);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (userId === undefined) return;

    // Gast (kein Login): lokaler Speicher statt Supabase - siehe
    // lib/local-portfolio/store.ts (Feedback-Runde 2, Punkt 5).
    if (userId === null) {
      // setState nicht synchron im Effect-Body (react-hooks/set-state-in-effect).
      const id = setTimeout(() => setIsSaved(isInLocalWatchlist(symbol)), 0);
      return () => clearTimeout(id);
    }

    let cancelled = false;
    const supabase = createClient();
    supabase
      .from("watchlists")
      .select("id")
      .eq("symbol", symbol)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setIsSaved(!!data);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, symbol]);

  async function handleClick() {
    if (userId === null) {
      // Bereits lokal gespeichert (z.B. von vor dieser Aenderung): Entfernen bleibt
      // ohne Konto moeglich. Neu hinzufuegen verlangt seit 2026-07-17 ein kostenloses
      // Konto (Erik: "Watchlist dauerhaft sichern... verlangt ein kostenloses Konto"),
      // statt weiterhin still im localStorage zu landen.
      if (isSaved) {
        removeLocalWatchlistSymbol(symbol);
        setIsSaved(false);
        return;
      }
      openAuthModal({
        mode: "signup",
        reason: `Erstelle ein kostenloses Konto, um ${symbol} dauerhaft und geräteübergreifend auf deiner Watchlist zu speichern.`,
      });
      return;
    }
    if (!userId) return;

    setIsPending(true);
    const supabase = createClient();

    if (isSaved) {
      const { error } = await supabase
        .from("watchlists")
        .delete()
        .eq("symbol", symbol)
        .eq("user_id", userId);
      if (!error) {
        setIsSaved(false);
        router.refresh();
      }
    } else {
      const { error } = await supabase.from("watchlists").insert({ symbol, user_id: userId });
      if (!error) {
        setIsSaved(true);
        router.refresh();
      }
    }
    setIsPending(false);
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={handleClick}
      disabled={isPending || userId === undefined}
      aria-pressed={isSaved}
      aria-label={isSaved ? "Von Watchlist entfernen" : "Zur Watchlist hinzufügen"}
      title={isSaved ? "Von Watchlist entfernen" : "Zur Watchlist hinzufügen"}
    >
      <Star className={cn("size-4", isSaved && "fill-gold-text text-gold-text")} />
    </Button>
  );
}
